"use client";
import { useState, useEffect } from "react";
import { Card, CardContent, IconButton, Typography, Box } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  PlayArrow,
  Pause,
} from "@mui/icons-material";
import { useDictionary } from "@/components/DictProvider/DictProvider";

interface YouTubeViewerProps {
  youtubeURLs: string[];
}

// Function to extract video ID from YouTube URL
const getVideoId = (url: string): string => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : "";
};

export default function YouTubeViewer({ youtubeURLs }: YouTubeViewerProps) {
  const dict = useDictionary();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoData, setVideoData] = useState<
    Array<{ id: string; title: string }>
  >([]);
  const [currentPage, setCurrentPage] = useState(0);
  const videosPerPage = 4; // Show 4 thumbnails per page

  useEffect(() => {
    // Extract video IDs and create video data
    const data = youtubeURLs.map((url, index) => ({
      id: getVideoId(url),
      title: `${dict.poductDescriptionPage.video} ${index + 1}`,
    }));
    setVideoData(data);
  }, [youtubeURLs, dict]);

  useEffect(() => {
    const newPage = Math.floor(currentIndex / videosPerPage);
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  }, [currentIndex, videosPerPage, currentPage]);

  const nextVideo = () => {
    setCurrentIndex((prev) => (prev + 1) % youtubeURLs.length);
    setIsPlaying(false);
  };

  const prevVideo = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + youtubeURLs.length) % youtubeURLs.length
    );
    setIsPlaying(false);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  if (videoData.length === 0) return null;

  const currentVideoId = videoData[currentIndex]?.id;

  return (
    <Box sx={{ width: "100%", maxWidth: 800, mx: "auto" }}>
      {/* Main Video Player */}
      <Card
        elevation={1}
        sx={{
          mb: 2,
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              aspectRatio: "16/9",
              bgcolor: "grey.100",
            }}
          >
            {currentVideoId && (
              <iframe
                src={`https://www.youtube.com/embed/${currentVideoId}${
                  isPlaying ? "?autoplay=1&rel=0" : "?rel=0"
                }`}
                title={videoData[currentIndex]?.title}
                style={{ width: "100%", height: "100%", border: "none" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </Box>

          {/* Video Controls */}
          <Box sx={{ p: 2, bgcolor: "background.paper" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  {videoData[currentIndex]?.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dict.poductDescriptionPage.video} {currentIndex + 1} /{" "}
                  {youtubeURLs.length}
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                style={{ direction: "ltr" }}
              >
                <IconButton
                  onClick={prevVideo}
                  disabled={youtubeURLs.length <= 1}
                  size="small"
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <ChevronLeft />
                </IconButton>

                <IconButton
                  onClick={togglePlay}
                  size="small"
                  sx={{
                    bgcolor: "#AC7529",
                    color: "primary.contrastText",
                    "&:hover": { bgcolor: "#AC7529" },
                  }}
                >
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>

                <IconButton
                  onClick={nextVideo}
                  disabled={youtubeURLs.length <= 1}
                  size="small"
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
